// ============================================================
// Contrack AI — shared chrome + steps 1–4
// Faithful TSX port of the prototype's steps.jsx.
// Step 4 is wired to the app's REAL auth hooks (see Step4 below).
// ============================================================
import type { CSSProperties, MouseEvent, ReactNode } from "react";
import {
  Logo, Wordmark, IconArrow, IconCheck, IconEnvelope, IconSync,
  INDUSTRIES, GOALS, INVOICE_VOLUMES, PROJECT_COUNTS,
  COUNTRIES, COUNTRY_FLAGS, COUNTRY_CONTRACT_TYPES,
} from "./data";
import { useOnboardingLang } from "./i18n";

// ---- Top bar: logo / segmented progress / language toggle ----
export function TopBar({ step, totalSteps }: { step: number; totalSteps: number }) {
  // Language toggle is now functional — switches the whole app (browser-detected
  // by default), so an English browser lands in English with USD amounts.
  const { lang, setLang } = useOnboardingLang();
  return (
    <header className="topbar topbar-reveal">
      <div className="topbar-logo">
        <Logo scale={1} />
        <Wordmark />
      </div>
      <div className="progress" role="progressbar" aria-valuenow={step} aria-valuemax={totalSteps}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <span key={i} className="seg">
            <span className={"seg-fill" + (i < step ? " on" : "")} />
          </span>
        ))}
      </div>
      <div className="lang">
        <button className={"lang-opt" + (lang === "nb" ? " lang-on" : "")} type="button" onClick={() => setLang("nb")}>NO</button>
        <span className="lang-sep">|</span>
        <button className={"lang-opt" + (lang === "en" ? " lang-on" : "")} type="button" onClick={() => setLang("en")}>EN</button>
      </div>
    </header>
  );
}

// ---- Footer nav ----
export function FooterNav({ onBack, onNext, nextLabel, nextEnabled, pulse, showBack, onSkip, skipLabel }: {
  onBack: () => void; onNext: () => void; nextLabel: string; nextEnabled: boolean;
  pulse: boolean; showBack: boolean; onSkip?: (() => void) | null; skipLabel?: string;
}) {
  const { copy } = useOnboardingLang();
  return (
    <footer className="footer">
      <div className="footer-left">
        {showBack && (
          <button className="btn-ghost" type="button" onClick={onBack}>{copy.back}</button>
        )}
        {onSkip && (
          <button className="btn-link footer-skip" type="button" onClick={onSkip}>{skipLabel || copy.skip}</button>
        )}
      </div>
      <button
        className={"btn-primary" + (pulse ? " btn-wake" : "")}
        type="button"
        disabled={!nextEnabled}
        onClick={onNext}
      >
        {nextLabel} <IconArrow size={17} style={{ marginLeft: 2 }} />
      </button>
    </footer>
  );
}

// ---- Step heading ----
function StepHead({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="step-head">
      <h1 className="step-title">{title}</h1>
      {subtitle && <p className="step-sub">{subtitle}</p>}
    </div>
  );
}

// ---- Selectable check dot (top-right of selected cards) ----
function CheckDot({ on }: { on: boolean }) {
  return (
    <span className={"checkdot" + (on ? " checkdot-on" : "")}>
      {on && <IconCheck size={13} style={{ color: "#fff" }} />}
    </span>
  );
}

// ---- Cursor-following tilt (barely perceptible, max ~1.5deg) ----
function handleTilt(e: MouseEvent<HTMLButtonElement>) {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  const px = (e.clientX - r.left) / r.width - 0.5;
  const py = (e.clientY - r.top) / r.height - 0.5;
  el.style.setProperty("--ry", (px * 3).toFixed(2) + "deg");
  el.style.setProperty("--rx", (-py * 3).toFixed(2) + "deg");
}
function clearTilt(e: MouseEvent<HTMLButtonElement>) {
  const el = e.currentTarget;
  el.style.setProperty("--rx", "0deg");
  el.style.setProperty("--ry", "0deg");
}

// ============================================================
// STEP COUNTRY — marked (single select). Steg 1.
// ============================================================
export function StepCountry({ value, onChange }: {
  value: string | null; onChange: (id: string) => void;
}) {
  const { copy } = useOnboardingLang();
  return (
    <div className="step-body">
      <StepHead title={copy.stepCountryTitle} subtitle={copy.stepCountrySub} />
      <div className="grid-3 stagger">
        {COUNTRIES.map((c) => {
          const on = value === c.id;
          return (
            <button key={c.id} type="button"
              className={"card card-select" + (on ? " is-selected" : "")}
              onClick={() => onChange(c.id)} onMouseMove={handleTilt} onMouseLeave={clearTilt}>
              <CheckDot on={on} />
              <span className="card-ico">
                <span style={{
                  display: "inline-flex", width: 34, height: 23, borderRadius: 5,
                  overflow: "hidden", border: "1px solid rgba(0,0,0,0.12)",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.07)",
                }}>{COUNTRY_FLAGS[c.id]}</span>
              </span>
              <span className="card-title">{copy.countries[c.id]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// STEP CONTRACT TYPE — kontraktsstandard (single select, filtrert til landet). Steg 2.
// ============================================================
export function StepContractType({ country, value, onChange }: {
  country: string | null; value: string | null; onChange: (id: string) => void;
}) {
  const { copy } = useOnboardingLang();
  const types = (country && COUNTRY_CONTRACT_TYPES[country]) || [];
  return (
    <div className="step-body">
      <StepHead title={copy.stepContractTitle} subtitle={copy.stepContractSub} />
      <div className="grid-3 stagger">
        {types.map((tp) => {
          const on = value === tp;
          const c = copy.contractTypes[tp];
          return (
            <button key={tp} type="button"
              className={"card card-select" + (on ? " is-selected" : "")}
              onClick={() => onChange(tp)} onMouseMove={handleTilt} onMouseLeave={clearTilt}>
              <CheckDot on={on} />
              <span className="card-title">{c?.title || tp}</span>
              <span className="card-desc">{c?.desc || ""}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// STEP 1 — Industry (single select)
// ============================================================
export function Step1({ value, otherText, onChange, onOtherText }: {
  value: string | null; otherText: string; onChange: (id: string) => void; onOtherText: (v: string) => void;
}) {
  const { copy } = useOnboardingLang();
  return (
    <div className="step-body">
      <StepHead title={copy.step1Title} subtitle={copy.step1Sub} />
      <div className="grid-3 stagger">
        {INDUSTRIES.map((it) => {
          const Ico = it.icon;
          const on = value === it.id;
          const isOther = it.id === "annet";
          const c = copy.industries[it.id];
          return (
            <button
              key={it.id}
              type="button"
              className={"card card-select" + (on ? " is-selected" : "") + (isOther && on ? " card-tall" : "")}
              onClick={() => onChange(it.id)}
              onMouseMove={handleTilt}
              onMouseLeave={clearTilt}
            >
              <CheckDot on={on} />
              <span className="card-ico"><Ico size={24} /></span>
              <span className="card-title">{c.title}</span>
              <span className="card-desc">{c.desc}</span>
              {isOther && on && (
                <input
                  className="inline-input"
                  type="text"
                  autoFocus
                  placeholder={copy.otherPlaceholder}
                  value={otherText}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => onOtherText(e.target.value)}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// STEP 2 — Goals (multi, max 2)
// ============================================================
export function Step2({ value, onToggle }: { value: string[]; onToggle: (id: string) => void }) {
  const { copy } = useOnboardingLang();
  const atMax = value.length >= 2;
  return (
    <div className="step-body">
      <StepHead title={copy.step2Title} subtitle={copy.step2Sub} />
      <div className="rows stagger">
        {GOALS.map((g) => {
          const Ico = g.icon;
          const on = value.includes(g.id);
          const disabled = !on && atMax;
          const c = copy.goals[g.id];
          return (
            <button
              key={g.id}
              type="button"
              className={"row row-select" + (on ? " is-selected" : "") + (disabled ? " is-muted" : "")}
              onClick={() => onToggle(g.id)}
            >
              <span className="row-ico" style={{ color: "#18181B", background: "#F4F4F5", border: "1px solid rgba(0,0,0,0.06)" }}>
                <Ico size={22} />
              </span>
              <span className="row-text">
                <span className="row-title">{c.title}</span>
                <span className="row-desc">{c.desc}</span>
              </span>
              <CheckDot on={on} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// STEP 3 — Volume (skippable)
// ============================================================
function PillGroup({ options, value, onSelect }: { options: string[]; value: string | null; onSelect: (o: string) => void }) {
  return (
    <div className="pills">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          className={"pill" + (value === o ? " pill-on" : "")}
          onClick={() => onSelect(o)}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

export function Step3({ invoices, projects, onInvoices, onProjects }: {
  invoices: string | null; projects: string | null; onInvoices: (o: string) => void; onProjects: (o: string) => void;
}) {
  const { copy } = useOnboardingLang();
  return (
    <div className="step-body">
      <StepHead title={copy.step3Title} />
      <div className="qstack stagger">
        <div className="q-block">
          <p className="q-label">{copy.step3Q1}</p>
          <PillGroup options={INVOICE_VOLUMES} value={invoices} onSelect={onInvoices} />
        </div>
        <div className="q-block">
          <p className="q-label">{copy.step3Q2}</p>
          <PillGroup options={PROJECT_COUNTS} value={projects} onSelect={onProjects} />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// STEP 4 — Connect invoice sources
// ============================================================
// Visually identical to the prototype's toggle cards. The card is a
// role="button" div (instead of a <button>) so the brand chips can be
// real, clickable OAuth triggers nested inside it.
function ConnectCard({ active, onToggle, icon, title, body, sub, badge, brands, connectedLabel }: {
  active: boolean; onToggle: () => void; icon: ReactNode; title: string; body: string;
  sub?: string; badge?: string; brands?: ReactNode; connectedLabel: string;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      className={"connect-card" + (active ? " is-connected" : "")}
      onClick={onToggle}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
    >
      <div className="connect-top">
        <span className="connect-ico">{icon}</span>
        {active ? (
          <span className="connect-status">
            <span className="status-dot" /> {connectedLabel}
          </span>
        ) : (
          <span className="connect-toggle"><span className="connect-toggle-knob" /></span>
        )}
      </div>
      <div className="connect-title">{title}</div>
      <div className="connect-body">{body}</div>
      {brands && <div className="connect-brands">{brands}</div>}
      {sub && <div className="connect-sub">{sub}</div>}
      {badge && <span className="connect-badge">{badge}</span>}
    </div>
  );
}

// Static brand chip (prototype look)
function BrandChip({ label }: { label: string }) {
  return <span className="brand-chip">{label}</span>;
}
// Interactive brand chip — starts a REAL OAuth connect. Visually identical
// to BrandChip; only difference is the cursor/hover and the click handler.
function BrandChipAction({ label, onClick, style }: { label: string; onClick: () => void; style?: CSSProperties }) {
  return (
    <button
      type="button"
      className="brand-chip brand-chip-action"
      style={style}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
    >
      {label}
    </button>
  );
}

export function Step4({ email, erp, onToggleEmail, onToggleErp, onConnectProvider }: {
  email: boolean; erp: boolean; onToggleEmail: () => void; onToggleErp: () => void;
  onConnectProvider: (provider: "gmail" | "outlook") => void;
}) {
  const { copy } = useOnboardingLang();
  return (
    <div className="step-body">
      <StepHead title={copy.step4Title} subtitle={copy.step4Sub} />
      <div className="connect-grid stagger">
        <ConnectCard
          active={email}
          onToggle={onToggleEmail}
          icon={<IconEnvelope size={26} />}
          title={copy.emailTitle}
          body={copy.emailBody}
          connectedLabel={copy.connected}
          // Real auth hooks: tapping a brand chip starts that provider's OAuth.
          brands={
            <>
              <BrandChipAction label="Gmail" onClick={() => onConnectProvider("gmail")} />
              <BrandChipAction label="Outlook" onClick={() => onConnectProvider("outlook")} />
            </>
          }
        />
        <ConnectCard
          active={erp}
          onToggle={onToggleErp}
          icon={<IconSync size={26} />}
          title={copy.erpTitle}
          body={copy.erpBody}
          connectedLabel={copy.connected}
          // TODO: Tripletex har ingen OAuth-redirect — ekte tilkobling krever
          // employee-token (admin) via Innstillinger → ERP (/erp/connect).
          // Her er kortet en visuell veksling; reell status reflekteres via /erp/status.
          brands={<BrandChip label="Tripletex" />}
          badge={copy.erpBadge}
        />
      </div>
    </div>
  );
}
